import { HomeAssistantConfig } from '../types/database.types';

const HA_CONFIG_KEY = 'zac_home_assistant_config';
export const DEFAULT_HA_TOKEN = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJkNTdhMzE0ZDZmMjM0OTUxODI0OGIxY2I2ZDY1OTdkNiIsImlhdCI6MTc4OTU3MTk2NywiZXhwIjoyMTA0OTMxOTY3fQ.FaqGkkNyk1ZT3ygU8Vr6QsSeUFmMXsx5PBIZCK95HfY';
export const DEFAULT_HA_URL = 'https://eduardofin.duckdns.org/ha';
export const LOCAL_HA_URL = 'http://192.168.1.100:8123';

export function getHAConfig(): HomeAssistantConfig {
  const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';
  const saved = localStorage.getItem(HA_CONFIG_KEY);
  if (saved) {
    try {
      const parsed = JSON.parse(saved);
      // Garante enabled sempre ativo por padrão
      parsed.enabled = parsed.enabled !== false;

      // Auto-migra configurações salvas antigas ou vazias
      if (!parsed.token || parsed.token.length < 20) {
        parsed.token = DEFAULT_HA_TOKEN;
      }
      if (parsed.echoDotSala === 'media_player.echo_dot_sala' || !parsed.echoDotSala) {
        parsed.echoDotSala = 'notify.echo_dot_da_sala_announce';
      }
      if (parsed.echoDotOutro === 'media_player.echo_dot' || !parsed.echoDotOutro) {
        parsed.echoDotOutro = 'notify.echo_dot_announce';
      }
      // Se estiver em ambiente HTTPS e a URL não for HTTPS, migra obrigatoriamente para o proxy DuckDNS seguro
      if (isHttps && (!parsed.url || !parsed.url.startsWith('https://'))) {
        parsed.url = DEFAULT_HA_URL;
      }
      return parsed;
    } catch (e) {
      console.error('Erro ao ler config salva do HA', e);
    }
  }

  return {
    url: isHttps ? DEFAULT_HA_URL : (import.meta.env.VITE_HOME_ASSISTANT_URL || LOCAL_HA_URL),
    token: import.meta.env.VITE_HOME_ASSISTANT_TOKEN || DEFAULT_HA_TOKEN,
    echoDotSala: 'notify.echo_dot_da_sala_announce',
    echoDotOutro: 'notify.echo_dot_announce',
    webhookSecret: import.meta.env.HA_WEBHOOK_SECRET || 'zac-secret-token',
    enabled: true,
  };
}

export function saveHAConfig(config: HomeAssistantConfig): void {
  localStorage.setItem(HA_CONFIG_KEY, JSON.stringify(config));
}

/**
 * Testa a conexão com a REST API do Home Assistant
 */
export async function testHAConnection(config = getHAConfig()): Promise<{ success: boolean; message: string }> {
  if (!config.url || !config.token) {
    return { success: false, message: 'URL ou Token não configurados.' };
  }

  const cleanUrl = config.url.replace(/\/+$/, '');

  // Detecta bloqueio de Mixed Content (navegador em HTTPS não permite chamar HTTP inseguro)
  if (typeof window !== 'undefined' && window.location.protocol === 'https:' && cleanUrl.startsWith('http://')) {
    return {
      success: false,
      message: 'Bloqueio de Segurança: O app está em HTTPS e o navegador bloqueia chamadas a HTTP inseguro. Use a URL HTTPS segura: https://eduardofin.duckdns.org/ha'
    };
  }

  try {
    const res = await fetch(`${cleanUrl}/api/`, {
      method: 'GET',
      mode: 'cors',
      cache: 'no-store',
      headers: {
        'Authorization': `Bearer ${config.token}`,
      },
    });

    if (res.ok) {
      const data = await res.json();
      return { success: true, message: `Conectado com sucesso ao Home Assistant! (${data.message || 'API OK'})` };
    } else {
      return { success: false, message: `Erro HTTP ${res.status}: ${res.statusText}` };
    }
  } catch (err: any) {
    return { success: false, message: `Falha de rede ao conectar no Home Assistant: ${err.message}` };
  }
}

/**
 * Envia anúncio de voz TTS apenas ao Echo Dot secundário (sem a sala)
 */
export async function sendHATextToSpeech(message: string, config = getHAConfig()): Promise<boolean> {
  if (!config.enabled || !config.url || !config.token) return false;

  const cleanUrl = config.url.replace(/\/+$/, '');
  // Usa apenas o Echo Dot secundário/quarto conforme solicitado
  const targetSpeakers = [config.echoDotOutro || 'notify.echo_dot_announce'].filter(Boolean);

  if (targetSpeakers.length === 0) return false;

  let anySuccess = false;

  for (const speaker of targetSpeakers) {
    try {
      // 1. Tenta notify.send_message (método moderno e padrão do Home Assistant)
      const notifyRes = await fetch(`${cleanUrl}/api/services/notify/send_message`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${config.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          entity_id: speaker,
          message: message,
        }),
      });

      if (notifyRes.ok) {
        anySuccess = true;
        continue;
      }

      // 2. Tenta método direto por nome do serviço notify.<servico>
      const serviceName = speaker.replace(/^notify\./, '');
      const directRes = await fetch(`${cleanUrl}/api/services/notify/${serviceName}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${config.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: message,
        }),
      });

      if (directRes.ok) {
        anySuccess = true;
        continue;
      }

      // 3. Fallback para notify.alexa_media
      const alexaRes = await fetch(`${cleanUrl}/api/services/notify/alexa_media`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${config.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: message,
          data: {
            type: 'announce',
            method: 'all',
          },
          target: [speaker],
        }),
      });

      if (alexaRes.ok) {
        anySuccess = true;
      }
    } catch (err) {
      console.warn(`[Home Assistant] Erro ao enviar TTS para ${speaker}:`, err);
    }
  }

  return anySuccess;
}

/**
 * Envia notificação Push para o iPhone de Eduardo via app oficial do Home Assistant
 */
export async function sendHAiPhonePush(title: string, message: string, config = getHAConfig()): Promise<boolean> {
  if (!config.enabled || !config.url || !config.token) return false;

  const cleanUrl = config.url.replace(/\/+$/, '');

  try {
    const res = await fetch(`${cleanUrl}/api/services/notify/send_message`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${config.token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        entity_id: 'notify.iphone_de_eduardo',
        title: title,
        message: message,
      }),
    });

    return res.ok;
  } catch (err) {
    console.warn('[Home Assistant] Erro ao enviar push para iPhone:', err);
    return false;
  }
}

/**
 * Dispara alerta de hidratação por voz no Echo Dot e push no iPhone via Home Assistant
 */
export async function triggerHAHydrationAlert(doseNumber: number, remainingMl: number): Promise<void> {
  const tts = `Hora da água.`;
  await sendHATextToSpeech(tts);
  await sendHAiPhonePush('Hora da água', 'Beba 400ml.');
}

/**
 * Dispara alarme anti-procrastinação de treino por voz no Echo Dot e push no iPhone
 */
export async function triggerHAWorkoutAlert(): Promise<void> {
  const tts = `Hora do treino.`;
  await sendHATextToSpeech(tts);
  await sendHAiPhonePush('Hora do treino', 'São 19:30. Inicie sua sessão de treino.');
}

/**
 * Consulta a quantidade de água consumida no Home Assistant (input_number.zac_agua_consumida)
 */
export async function fetchHAWaterMl(config = getHAConfig()): Promise<number | null> {
  if (!config.url || !config.token) return null;

  let cleanUrl = config.url.replace(/\/+$/, '');
  const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';
  if (isHttps && cleanUrl.startsWith('http://')) {
    cleanUrl = DEFAULT_HA_URL;
  }

  try {
    const res = await fetch(`${cleanUrl}/api/states/input_number.zac_agua_consumida`, {
      method: 'GET',
      mode: 'cors',
      cache: 'no-store',
      headers: {
        'Authorization': `Bearer ${config.token}`,
      },
    });

    if (res.ok) {
      const data = await res.json();
      const val = parseFloat(data.state);
      return isNaN(val) ? null : Math.round(val);
    }
    console.warn('[Home Assistant] Resposta não OK ao consultar água:', res.status, res.statusText);
    return null;
  } catch (err) {
    console.warn('[Home Assistant] Falha ao consultar água consumida:', err);
    return null;
  }
}

/**
 * Atualiza o contador de água no Home Assistant (input_number.zac_agua_consumida)
 */
export async function syncHAWaterMl(amountMl: number, config = getHAConfig()): Promise<boolean> {
  if (!config.url || !config.token) return false;

  let cleanUrl = config.url.replace(/\/+$/, '');
  const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';
  if (isHttps && cleanUrl.startsWith('http://')) {
    cleanUrl = DEFAULT_HA_URL;
  }

  try {
    const res = await fetch(`${cleanUrl}/api/services/input_number/set_value`, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Authorization': `Bearer ${config.token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        entity_id: 'input_number.zac_agua_consumida',
        value: amountMl,
      }),
    });

    return res.ok;
  } catch (err) {
    console.warn('[Home Assistant] Falha ao sincronizar água consumida:', err);
    return false;
  }
}

